/**
 * Routine-draft form validation — the required-field checks the editor enforces before Save / Preview.
 *
 * Client-side this is "is the field filled in" only; deeper SQL rules (the required LIMIT + time-window
 * bindings) stay server-side and surface via the dry-run step errors. Both the editor (for inline
 * display) and the actions layer (to block Save/Preview) call {@link validateRoutineDraft}, so they
 * can never disagree.
 */
import type { Condition, WorkflowDraft } from '../types';

/**
 * Validation error for a condition's threshold value, or `null` when valid/absent. An emptied field is
 * rejected here (rather than silently coerced to 0 by `conditionToConfig`) so Save / Run block with a
 * clear reason instead of persisting a `row_count <op> 0` the user never intended.
 */
function conditionValueError(condition: Condition | null): string | null {
  if (!condition) return null;
  if (condition.value.trim() === '') return 'Enter a threshold value';
  return null;
}

/** A required field of the routine draft, keyed by its draft field (used for touched-tracking + focus). */
export type RoutineField = 'warehouseId' | 'sql' | 'condition' | 'channel' | 'template';

type RoutineValidation = Partial<Record<RoutineField, string>>;

/** Editor order of the fields — drives "focus the FIRST invalid field" on a blocked submit. */
const ROUTINE_FIELD_ORDER: readonly RoutineField[] = ['warehouseId', 'sql', 'condition', 'channel', 'template'];

/** DOM id of each field's focusable control (set on the step components), for scroll-to + focus. */
const FIELD_DOM_ID: Record<RoutineField, string> = {
  warehouseId: 'query-warehouse',
  sql: 'query-sql',
  condition: 'condition-value',
  channel: 'slack-channel',
  template: 'slack-template',
};

/** Required-field validation for a routine draft — an entry per field that is empty/invalid. */
export function validateRoutineDraft(draft: WorkflowDraft): RoutineValidation {
  const errors: RoutineValidation = {};
  if (!draft.warehouseId) errors.warehouseId = 'Select a warehouse.';
  if (!draft.sql.trim()) errors.sql = 'Add a query.';
  const condition = conditionValueError(draft.condition);
  if (condition) errors.condition = condition;
  if (!draft.channel) errors.channel = 'Select a channel.';
  if (!draft.template.trim()) errors.template = 'Add a message.';
  return errors;
}

export function hasValidationErrors(errors: RoutineValidation): boolean {
  return Object.keys(errors).length > 0;
}

/** The first invalid field in editor order, or null when the draft is valid. */
export function firstInvalidField(errors: RoutineValidation): RoutineField | null {
  return ROUTINE_FIELD_ORDER.find((f) => errors[f]) ?? null;
}

/** Scroll + focus the first invalid field's control — called on a blocked Save/Preview. */
export function focusFirstInvalidField(errors: RoutineValidation): void {
  const field = firstInvalidField(errors);
  if (!field) return;
  const el = document.getElementById(FIELD_DOM_ID[field]);
  el?.scrollIntoView({ block: 'center' });
  el?.focus();
}
